import { useEffect, useMemo, useState } from 'react';
import { roomInviteService, MyRoomSummary } from '../../services/api/roomInviteService';
import './RoomPickerSheet.css';

interface RoomPickerSheetProps {
  /** Who we're inviting (for the header). */
  targetName: string;
  /** The room we're currently in — excluded from the list (can't invite to here). */
  currentRoomId?: string;
  onPick: (room: MyRoomSummary) => void;
  onClose: () => void;
}

/**
 * P3 — the "invite to a room" picker. Saved rooms (★) sort above visited (🕐).
 * Full rooms are disabled. Reached from the PlayerSheet's 🚪 action.
 */
export function RoomPickerSheet({ targetName, currentRoomId, onPick, onClose }: RoomPickerSheetProps) {
  const [saved, setSaved] = useState<MyRoomSummary[]>([]);
  const [visited, setVisited] = useState<MyRoomSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    roomInviteService.getMyRooms().then((list) => {
      if (cancelled) return;
      const drop = (r: MyRoomSummary) => r.id !== currentRoomId;
      setSaved(list.saved.filter(drop));
      setVisited(list.visited.filter(drop));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [currentRoomId]);

  const filter = (rooms: MyRoomSummary[]) => {
    const q = query.trim().toLowerCase();
    if (!q) return rooms;
    return rooms.filter((r) => r.name.toLowerCase().includes(q) || (r.category ?? '').includes(q));
  };
  const savedFiltered = useMemo(() => filter(saved), [saved, query]);
  const visitedFiltered = useMemo(() => filter(visited), [visited, query]);

  const isFull = (r: MyRoomSummary) => r.currentPlayers + 2 > r.maxPlayers;

  const renderRow = (r: MyRoomSummary) => {
    const full = isFull(r);
    return (
      <button
        key={r.id}
        className={`room-pick-row ${full ? 'full' : ''}`}
        disabled={full}
        onClick={() => !full && onPick(r)}
        title={full ? 'Not enough room for both of you' : `Invite ${targetName} to ${r.name}`}
      >
        <span className="room-pick-name">{r.name}</span>
        <span className="room-pick-meta">
          {r.category && <span className={`room-pick-chip cat-${r.category}`}>{r.category}</span>}
          <span className="room-pick-players">👥 {r.currentPlayers}/{r.maxPlayers}</span>
          {r.hasPassword && <span title="Password" aria-label="Password protected">🔑</span>}
          {r.requiresApproval && <span title="Knock to enter" aria-label="Requires approval">✋</span>}
          {full && <span className="room-pick-fullpill">full</span>}
        </span>
      </button>
    );
  };

  return (
    <div className="room-pick-backdrop" onClick={onClose}>
      <div className="room-pick-sheet" role="dialog" aria-label={`Invite ${targetName} to a room`} onClick={(e) => e.stopPropagation()}>
        <div className="room-pick-grabber" aria-hidden="true" />
        <div className="room-pick-header">
          <h3>Invite {targetName} to…</h3>
          <button className="room-pick-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <input
          className="room-pick-search"
          type="text"
          placeholder="Search your rooms…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <div className="room-pick-body">
          {loading && <div className="room-pick-empty">Loading your rooms…</div>}

          {!loading && savedFiltered.length === 0 && visitedFiltered.length === 0 && (
            <div className="room-pick-empty">
              Rooms you visit or save appear here. Save a room from its header ★ to pin it to the top.
            </div>
          )}

          {savedFiltered.length > 0 && (
            <section>
              <div className="room-pick-section">★ Saved</div>
              {savedFiltered.map(renderRow)}
            </section>
          )}

          {visitedFiltered.length > 0 && (
            <section>
              <div className="room-pick-section">🕐 Recent</div>
              {visitedFiltered.map(renderRow)}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default RoomPickerSheet;
